import React, {Component} from 'react';
import ReactDom from 'react-dom';
import propTypes from 'prop-types';
import {X} from 'lucide-react';
import clsx from 'clsx';
import {twMerge} from 'tailwind-merge';
import Button from 'components/ui/Button';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import SpacedItems from './SpacedItems';
import {screenXsMax} from 'styles/constants';

class ModalComponent extends Component {
	state = {animationPending: true};

	backgroundComponentRef = React.createRef();

	hide = () => {
		if (this.props.hide) this.props.hide();
	};

	onKeyDown = event => {
		if (event.key === 'Escape') this.hide();
	};

	hideOnBackgroundClick = event => {
		const bgEl = this.backgroundComponentRef.current;
		if (bgEl && bgEl === event.target) {
			this.hide();
		}
	};

	componentDidMount() {
		setTimeout(() => this.setState({animationPending: false}));
	}

	render() {
		const {
			children,
			contentClassName,
			headerClassName,
			footerClassName,
			closeBtnClassName,
			header,
			headerTitle,
			headerActions,
			footer,
			footerLeft,
			cancelButton,
			confirmButton,
			maxWidth = screenXsMax,
			hide,
			onKeyDown,
			className,
			...rest
		} = this.props;

		const closeBtn = hide && (
			<Button
				type="button"
				size="sm"
				variant="ghost"
				className={closeBtnClassName}
				onClick={this.hide}
			>
				<X className="size-4" />
			</Button>
		);

		const headerClasses = twMerge(
			'flex items-center justify-between gap-2 rounded-t-md border-b border-gray-200 bg-white px-5 py-3',
			headerClassName,
		);

		return (
			<div
				tabIndex="-1"
				onKeyDown={this.onKeyDown}
				className={twMerge('relative', className)}
				{...rest}
			>
				<div
					className={clsx(
						'fixed inset-0 z-[100] bg-black transition-opacity duration-100 ease-in',
						this.state.animationPending ? 'opacity-0' : 'opacity-40',
					)}
				/>
				<div
					ref={this.backgroundComponentRef}
					onClick={this.hideOnBackgroundClick}
					className="fixed inset-0 bottom-[1px] overflow-y-auto z-[110] flex items-start justify-center p-2"
				>
					<div
						style={{maxWidth}}
						className={clsx(
							'w-full rounded-md bg-white shadow-lg transition-[top] duration-100 ease-in flex flex-col',
							this.state.animationPending && '-mt-[10px]',
						)}
					>
						{header ? (
							<div className={headerClasses}>
								{header}
								{closeBtn}
							</div>
						) : headerTitle || headerActions ? (
							<div className={headerClasses}>
								<ModalHeader title={headerTitle} actions={headerActions} />
								{closeBtn}
							</div>
						) : null}

						<div className={twMerge('flex-1 py-5 px-6', contentClassName)}>
							{children}
						</div>

						{(footer || footerLeft || cancelButton || confirmButton) && (
							<div
								className={twMerge(
									'rounded-b-md border-t border-gray-200 bg-gray-50 px-5 py-[15px]',
									footerClassName,
								)}
							>
								{footer || (
									<ModalFooter
										left={footerLeft}
										right={
											<SpacedItems>
												{cancelButton}
												{confirmButton}
											</SpacedItems>
										}
									/>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

ModalComponent.propTypes = {
	show: propTypes.bool,
	size: propTypes.string,
	maxWidth: propTypes.string,
	topPadding: propTypes.bool,
	bottomPadding: propTypes.bool,
	verticalPadding: propTypes.bool,
	hide: propTypes.func,
	onKeyDown: propTypes.func,
	children: propTypes.node,
	header: propTypes.node,
	headerTitle: propTypes.node,
	headerActions: propTypes.node,
	footer: propTypes.node,
	footerLeft: propTypes.node,
	cancelButton: propTypes.node,
	confirmButton: propTypes.node,
	className: propTypes.string,
};

const Modal = ({parentNode, ...rest}) => {
	return ReactDom.createPortal(
		<ModalComponent {...rest} />,
		parentNode || document.getElementById('app'),
	);
};

export default Modal;
